import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, Chip, IconButton, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { ArrowBack, PictureAsPdf, Undo, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fmt, sym } from '../utils/currency';

const DOC_COLOR = { Invoice: 'warning', Payment: 'success', Reversed: 'default', Cancelled: 'default' };

const SupplierLedger = () => {
  const { supplierCode } = useParams();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [closingBalance, setClosingBalance] = useState(0);
  const [supplier, setSupplier] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [reverseDlg, setReverseDlg] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // Auto-dismiss success after 1 s
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 1000);
    return () => clearTimeout(t);
  }, [success]);

  const fetchLedger = useCallback(async () => {
    setLoading(true);
    try {
      const [ledgerRes, suppliersRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/payables/suppliers/${supplierCode}/ledger`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/payables/suppliers`, { headers })
      ]);
      setEntries(ledgerRes.data.entries);
      setClosingBalance(ledgerRes.data.closingBalance);
      const s = suppliersRes.data.find(x => x.supplierCode === supplierCode);
      setSupplier(s || { supplierCode, supplierName: supplierCode });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [supplierCode]); // eslint-disable-line

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  const confirmReverse = async () => {
    if (!reverseDlg) return;
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/payables/vouchers/${reverseDlg}/reverse`,
        {},
        { headers }
      );
      setSuccess(data.message); setReverseDlg(null);
      fetchLedger();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reverse voucher');
    }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    const name = supplier ? `${supplier.supplierName} (${supplierCode})` : supplierCode;

    doc.setFontSize(16);
    doc.text('Vendor Statement', 14, 18);
    doc.setFontSize(11);
    doc.text(name, 14, 26);
    doc.text(`Printed: ${new Date().toLocaleDateString()}`, 14, 33);
    doc.text(
      `Closing Balance: ${sym()} ${fmt(closingBalance)}${closingBalance > 0 ? ' (Amount Owed)' : ' (Credit)'}`,
      14, 40
    );

    autoTable(doc, {
      head: [['Date', 'Document No', 'Type', 'Description', `Debit (${sym()})`, `Credit (${sym()})`, `Balance (${sym()})`, 'Status']],
      body: entries.map(e => [
        new Date(e.date).toLocaleDateString(),
        e.documentNo,
        e.documentType,
        e.description,
        e.debit > 0 ? e.debit.toFixed(2) : '',
        e.credit > 0 ? e.credit.toFixed(2) : '',
        e.balance.toFixed(2),
        e.status
      ]),
      startY: 48,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [230, 81, 0] },
      alternateRowStyles: { fillColor: [250, 250, 250] }
    });

    doc.save(`Vendor_Statement_${supplierCode}.pdf`);
  };

  if (loading) return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading ledger...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Button startIcon={<ArrowBack />} onClick={() => navigate('/suppliers')} sx={{ mr: 2 }}>
                Back
              </Button>
              <Box>
                <Typography variant="h5" color="primary">Vendor Ledger</Typography>
                <Typography variant="body2" color="text.secondary">
                  {supplier?.supplierName} — {supplierCode}
                </Typography>
              </Box>
            </Box>
            <IconButton color="error" onClick={exportPDF} title="Export Statement to PDF"
              sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
              <PictureAsPdf />
            </IconButton>
          </Box>

          {/* Balance summary */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <Chip
              label={`Balance: ${sym()} ${fmt(closingBalance)}`}
              color={closingBalance > 0 ? 'error' : closingBalance < 0 ? 'success' : 'default'}
              variant="outlined"
              sx={{ fontWeight: 'bold', fontSize: '1rem', px: 1 }}
            />
            {closingBalance > 0 && (
              <Typography variant="body2" color="error" sx={{ alignSelf: 'center' }}>
                Amount owed to supplier
              </Typography>
            )}
            {closingBalance < 0 && (
              <Typography variant="body2" color="success.main" sx={{ alignSelf: 'center' }}>
                Credit balance (overpaid)
              </Typography>
            )}
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {entries.length === 0 ? (
            <Typography color="text.secondary">No transactions for this supplier yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Document No</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell align="right"><strong>Debit ({sym()})</strong></TableCell>
                  <TableCell align="right"><strong>Credit ({sym()})</strong></TableCell>
                  <TableCell align="right"><strong>Balance ({sym()})</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e, i) => (
                  <TableRow
                    key={`${e.documentNo}-${i}`}
                    hover
                    sx={{ opacity: e.status === 'Reversed' || e.status === 'Cancelled' ? 0.5 : 1 }}
                  >
                    <TableCell>{new Date(e.date).toLocaleDateString()}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{e.documentNo}</TableCell>
                    <TableCell>
                      <Chip
                        label={e.documentType}
                        color={DOC_COLOR[e.status] || DOC_COLOR[e.documentType] || 'default'}
                        size="small" variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{e.description}</TableCell>
                    <TableCell align="right" sx={{ color: e.debit > 0 ? 'error.main' : 'inherit' }}>
                      {e.debit > 0 ? e.debit.toFixed(2) : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ color: e.credit > 0 ? 'success.main' : 'inherit' }}>
                      {e.credit > 0 ? e.credit.toFixed(2) : ''}
                    </TableCell>
                    <TableCell align="right"
                      sx={{ fontWeight: i === entries.length - 1 ? 700 : 'normal' }}>
                      {e.balance.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={e.status}
                        color={e.status === 'Posted' || e.status === 'Open' ? 'success'
                          : e.status === 'Partial' ? 'warning'
                          : e.status === 'Paid' ? 'info'
                          : 'default'}
                        size="small" variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {e.documentType === 'Payment' && e.status === 'Posted' && (
                        <IconButton size="small" color="warning" title="Reverse Voucher"
                          onClick={() => setReverseDlg(e.documentNo)}>
                          <Undo fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reverse Payment Confirmation Dialog */}
      <Dialog open={Boolean(reverseDlg)} onClose={() => setReverseDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningAmber color="warning" />
            Reverse Payment
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Reverse voucher <strong>{reverseDlg}</strong>? This will restore the invoice balance.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverseDlg(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmReverse}>Reverse</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierLedger;
